import { ErrorMessage, RadioCard, RadioCardProps } from "@justhome/react-ui";
import { ChangeEvent } from "react";
import {
  Control,
  FieldPath,
  FieldValues,
  Path,
  PathValue,
  UseControllerProps,
  useController,
} from "react-hook-form";

interface ControlledRadioCardGroupProps<T extends FieldValues>
  extends Omit<RadioCardProps, "children" | "type" | "value"> {
  name: FieldPath<T>;
  control: Control<T>;
  options: any[];
  rules?: UseControllerProps<T>["rules"];
  defaultValue?: PathValue<T, Path<T>>;
  label: string;
}

const ControlledRadioCardGroup = <T extends FieldValues>({
  options,
  control,
  name,
  size,
  rules,
  defaultValue,
  label,
  className,
  ...props
}: ControlledRadioCardGroupProps<T>) => {
  const {
    field: { onChange, ...field },
    fieldState,
  } = useController({
    rules,
    name,
    control,
    defaultValue,
  });

  return (
    <div className={className}>
      <label className="flex text-title-1 mb-xs">{label}</label>
      <div className="grid m:grid-cols-2 gap-x-3xs gap-y-3xs">
        {options.map((option) => (
          <RadioCard
            {...field}
            {...props}
            size="sm"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              e.target.checked && onChange(option.value)
            }
            checked={field.value === option.value}
            key={option.value}
            value={option.value}
            type="radio"
          >
            {option.label}
          </RadioCard>
        ))}

        {fieldState.error && (
          <ErrorMessage className="m:col-span-2">
            {fieldState.error.message}
          </ErrorMessage>
        )}
      </div>
    </div>
  );
};

export default ControlledRadioCardGroup;
